import { DatesRangeValue } from '@mantine/dates';
import {
  AllowanceModelDto,
  ClassDefinitionItemDto,
  FamilyUnit,
  OEPPaymentMethod,
  isDefined,
} from '@zorro/clients';
import {
  ALL_EMPLOYEES_CLASS_NAME,
  VALIDATION_MESSAGES,
  isCustomClassCategoryOnlyDefinition,
  logger,
  optionalNumber,
} from '@zorro/common-utils';
import {
  formatDateEnLocaleRange,
  formatDateTimeISOUtc,
  getNow,
  getToday,
  isBusinessDay,
  parseDate,
  parseDateUnknown,
} from '@zorro/shared/formatters';
import { exportToCsv, showErrorNotification } from '@zorro/web/ui-utils';
import compact from 'lodash/compact';
import * as yup from 'yup';

export const ALLOWANCE_MODEL_SOURCE_NOTE_MAX_LENGTH = 5000;

export enum CoverageScope {
  MEDICAL_ONLY = 'MEDICAL_ONLY',
  EXPANDED = 'EXPANDED',
}

export const getPlanYearSetupSchema = (
  isFinalizationMode: boolean,
  isLynxPaymentsEnabled: boolean = false,
  isAllowanceModelRequired: boolean = true,
  savedValues: { initialDrawDate?: string | null } = {}
) => {
  const isLynxFieldRequired = isLynxPaymentsEnabled && isFinalizationMode;

  return yup.object({
    initialDrawDate: (() => {
      const baseSchema = yup
        .date()
        .test(
          'is-business-day',
          VALIDATION_MESSAGES.initialDrawDateMustBeBusinessDay,
          (value) => !value || isBusinessDay(value)
        )
        .test(
          'is-future-date',
          VALIDATION_MESSAGES.initialDrawDateMustBeFuture,
          (value) => {
            if (!value) return true;
            if (
              savedValues.initialDrawDate &&
              parseDateUnknown(value).isSame(
                parseDateUnknown(savedValues.initialDrawDate),
                'day'
              )
            ) {
              return true;
            }
            return parseDateUnknown(value).isAfter(getToday(), 'day');
          }
        );
      return isLynxFieldRequired
        ? baseSchema.required(VALIDATION_MESSAGES.initialDrawDateRequired)
        : baseSchema;
    })(),
    reserveAmount: isLynxFieldRequired
      ? yup
          .number()
          .required(VALIDATION_MESSAGES.reserveAmountRequired)
          .positive(VALIDATION_MESSAGES.reserveAmountPositive)
      : yup
          .number()
          .transform(optionalNumber)
          .positive(VALIDATION_MESSAGES.reserveAmountPositive),
    thresholdAmount: (() => {
      const baseSchema = yup
        .number()
        .transform(optionalNumber)
        .positive(VALIDATION_MESSAGES.thresholdAmountPositive)
        .test(
          'threshold-less-than-reserve',
          VALIDATION_MESSAGES.thresholdAmountMustBeLessThanReserve,
          function (value) {
            const { reserveAmount } = this.parent;
            if (isDefined(value) && isDefined(reserveAmount)) {
              return value < reserveAmount;
            }
            return true;
          }
        );
      return isLynxFieldRequired
        ? baseSchema.required(VALIDATION_MESSAGES.thresholdAmountRequired)
        : baseSchema;
    })(),
    isAORChanged: yup.boolean().required(VALIDATION_MESSAGES.isAORChanged),
    coverageScope: yup
      .mixed<CoverageScope>()
      .oneOf(Object.values(CoverageScope))
      .required(),
    dentalCoverage: yup.boolean().required(),
    visionCoverage: yup.boolean().required(),
    qmeCoverage: yup.boolean().required(),
    isDomesticPartnerAllowed: yup.boolean().required(),
    allowanceModel: isAllowanceModelRequired
      ? yup
          .mixed<File | AllowanceModelDto>()
          .required(VALIDATION_MESSAGES.allowanceModelRequired)
      : yup.mixed<File | AllowanceModelDto>(),
    allowanceModelSource: yup
      .object({
        quoteId: yup.string().nullable(),
        planDesignId: yup.string().nullable(),
        adjustmentNote: yup.string().nullable(),
      })
      .when('allowanceModel', {
        is: (allowanceModel: unknown) => allowanceModel instanceof File,
        then: (schema) =>
          schema.shape({
            planDesignId: yup
              .string()
              .nullable()
              .when('quoteId', {
                is: Boolean,
                then: (field) =>
                  field.required(
                    VALIDATION_MESSAGES.allowanceModelSourcePlanDesignRequired
                  ),
              }),
            adjustmentNote: yup
              .string()
              .nullable()
              .max(ALLOWANCE_MODEL_SOURCE_NOTE_MAX_LENGTH)
              .when('quoteId', {
                is: Boolean,
                then: (field) =>
                  field
                    .trim()
                    .required(
                      VALIDATION_MESSAGES.allowanceModelSourceNoteRequired
                    ),
              }),
          }),
      }),
    ...(isFinalizationMode
      ? {
          effectiveDates: yup
            .tuple([
              yup
                .date()
                .required(VALIDATION_MESSAGES.effectiveDateRequired)
                .nullable(),
              yup
                .date()
                .required(VALIDATION_MESSAGES.effectiveDateRequired)
                .nullable(),
            ])
            .required(),
          oepDates: yup
            .tuple([
              yup
                .date()
                .required(VALIDATION_MESSAGES.oepDateRequired)
                .nullable(),
              yup
                .date()
                .required(VALIDATION_MESSAGES.oepDateRequired)
                .nullable(),
            ])
            .required(),
          paymentMethod: yup
            .mixed<OEPPaymentMethod>()
            .oneOf(
              Object.values(OEPPaymentMethod),
              VALIDATION_MESSAGES.paymentMethodRequired
            )
            .typeError(VALIDATION_MESSAGES.paymentMethodRequired)
            .required(VALIDATION_MESSAGES.paymentMethodRequired),
          initialDrawAmount: yup.number().when('paymentMethod', {
            is: OEPPaymentMethod.ZORRO_PAY,
            then: (schema) =>
              schema
                .required(VALIDATION_MESSAGES.initialDrawAmountRequired)
                .positive(VALIDATION_MESSAGES.initialDrawAmountPositive),
            otherwise: (schema) =>
              schema
                .transform(optionalNumber)
                .positive(VALIDATION_MESSAGES.initialDrawAmountPositive),
          }),
          allowanceModelClassDefinitions: yup
            .object({
              isSuccess: yup.boolean(),
              message: yup.string(),
              classDefinitions: yup.array().nullable(),
            })
            .nullable()
            .test(
              'is-success-if-present',
              'Allowance model must be valid',
              (value) => value === null || value.isSuccess === true
            ),
        }
      : {
          effectiveDates: yup
            .tuple([
              yup
                .date()
                .required(VALIDATION_MESSAGES.effectiveDateRequired)
                .nullable(),
              yup
                .date()
                .required(VALIDATION_MESSAGES.effectiveDateRequired)
                .nullable(),
            ])
            .required(),
          oepDates: yup
            .tuple([
              yup
                .date()
                .required(VALIDATION_MESSAGES.oepDateRequired)
                .nullable(),
              yup
                .date()
                .required(VALIDATION_MESSAGES.oepDateRequired)
                .nullable(),
            ])
            .required(),
          initialDrawAmount: yup
            .number()
            .transform(optionalNumber)
            .positive(VALIDATION_MESSAGES.initialDrawAmountPositive),
          paymentMethod: yup
            .mixed<OEPPaymentMethod>()
            .oneOf(
              Object.values(OEPPaymentMethod),
              VALIDATION_MESSAGES.paymentMethodRequired
            )
            .typeError(VALIDATION_MESSAGES.paymentMethodRequired)
            .nullable(),
          allowanceModelClassDefinitions: yup
            .object({
              isSuccess: yup.boolean(),
              message: yup.string(),
              classDefinitions: yup.array().nullable(),
            })
            .nullable()
            .test(
              'is-success-if-present',
              'Allowance model must be valid',
              (value) => value === null || value.isSuccess === true
            ),
        }),
  });
};

export type PlanYearSetupFormFields = yup.InferType<
  ReturnType<typeof getPlanYearSetupSchema>
>;

export const getCoverageEndMonthBounds = (
  effectiveDates: PlanYearSetupFormFields['effectiveDates']
): { minDate?: Date; maxDate?: Date } => {
  const [coverageStart, coverageEnd] = effectiveDates ?? [];
  if (!coverageStart || coverageEnd) {
    return {};
  }
  return {
    minDate: coverageStart,
    maxDate: parseDate(coverageStart).endOf('year').toDate(),
  };
};

type AllowanceModelExportFields = {
  ageFrom: number;
  ageTo: number;
  class: string;
  [key: string]: number | string;
};

export interface ClassComponents {
  name: string;
  values: string;
}

export interface ProcessedClassDefinitions {
  isAllEmployeeOnly: boolean;
  classComponents: ClassComponents[];
}

export function processClassDefinitions(
  classDefinitions: ClassDefinitionItemDto[] | null | undefined
): ProcessedClassDefinitions {
  if (!classDefinitions) {
    return { isAllEmployeeOnly: false, classComponents: [] };
  }

  if (classDefinitions.length === 1) {
    const [
      {
        className,
        customClassCategory,
        state,
        ratingArea,
        employmentType,
        wageType,
      },
    ] = classDefinitions;
    const isAllEmployeeOnly =
      (className === ALL_EMPLOYEES_CLASS_NAME ||
        customClassCategory === ALL_EMPLOYEES_CLASS_NAME) &&
      !state &&
      !ratingArea &&
      !employmentType &&
      !wageType;

    if (isAllEmployeeOnly) {
      return { isAllEmployeeOnly: true, classComponents: [] };
    }
  }

  const classComponents: ClassComponents[] = [];

  if (classDefinitions.some((definition) => definition.state)) {
    const uniqueStates = [
      ...new Set(
        compact(classDefinitions.map((definition) => definition.state))
      ),
    ];
    classComponents.push({
      name: 'State',
      values: `State: ${uniqueStates.join(', ')}`,
    });
  }

  if (classDefinitions.some((definition) => definition.ratingArea)) {
    const uniqueRatingAreas = [
      ...new Set(
        compact(classDefinitions.map((definition) => definition.ratingArea))
      ),
    ];
    classComponents.push({
      name: 'Rating area',
      values: `Rating area: ${uniqueRatingAreas.join(', ')}`,
    });
  }

  if (classDefinitions.some((definition) => definition.employmentType)) {
    const uniqueEmploymentTypes = [
      ...new Set(
        compact(classDefinitions.map((definition) => definition.employmentType))
      ),
    ];
    const formattedTypes = uniqueEmploymentTypes.map((type) =>
      type === 'FULL_TIME' ? 'Full time' : 'Part time'
    );
    classComponents.push({
      name: 'Employment type',
      values: `Employment type: ${formattedTypes.join(', ')}`,
    });
  }

  if (classDefinitions.some((definition) => definition.wageType)) {
    const uniqueWageTypes = [
      ...new Set(
        compact(classDefinitions.map((definition) => definition.wageType))
      ),
    ];
    const formattedWageTypes = uniqueWageTypes.map((type) =>
      type === 'SALARY' ? 'Salary' : 'Hourly'
    );
    classComponents.push({
      name: 'Wage type',
      values: `Wage type: ${formattedWageTypes.join(', ')}`,
    });
  }

  if (classDefinitions.some((definition) => definition.customClassCategory)) {
    const uniqueCustom = [
      ...new Set(
        compact(
          classDefinitions.map((definition) => definition.customClassCategory)
        )
      ),
    ];

    const standaloneCategories = new Set(
      classDefinitions
        .filter(isCustomClassCategoryOnlyDefinition)
        .map((definition) => definition.customClassCategory)
    );

    const formattedCategories = uniqueCustom.map((category) =>
      standaloneCategories.has(category) ? `${category} (standalone)` : category
    );

    classComponents.push({
      name: 'Custom class category',
      values: `Custom class category: ${formattedCategories.join(', ')}`,
    });
  }

  return { isAllEmployeeOnly: false, classComponents };
}

export const renderNullableDateRange = (range?: DatesRangeValue): string => {
  if (!range?.[0] || !range?.[1]) return '-';

  return formatDateEnLocaleRange(range[0], range[1]);
};

export function exportAllowanceModelToCsv(
  allowanceModel: AllowanceModelDto,
  employerName = ''
) {
  try {
    const hasDependents = allowanceModel.items.some((item) =>
      isDefined(item.numberOfDependents)
    );

    const allowanceModelItemsWithRowId = allowanceModel.items.map((model) => ({
      ...model,
      rowId: `${model.ageFrom}_${model.ageTo}_${model.class}`,
      familyType:
        model.numberOfDependents === null ? 'max' : model.numberOfDependents,
    }));
    const exportableRowByRowId = allowanceModelItemsWithRowId.reduce(
      (group: { [rowId: string]: AllowanceModelExportFields }, item) => {
        group[item.rowId] = group[item.rowId] ?? {
          ageFrom: item.ageFrom,
          ageTo: item.ageTo,
          class: item.class,
        };
        let key = '';
        switch (item.familyUnit) {
          case FamilyUnit.EMPLOYEE_ONLY: {
            key = 'EE';
            break;
          }
          case FamilyUnit.EMPLOYEE_SPOUSE: {
            key = 'ES';
            break;
          }
          case FamilyUnit.EMPLOYEE_CHILD: {
            key = hasDependents ? `EC${item.familyType}` : 'EC';
            break;
          }
          case FamilyUnit.FAMILY: {
            key = hasDependents ? `FA${item.familyType}` : 'FA';
            break;
          }
        }
        if (key !== '') {
          group[item.rowId][key] = item.stipend;
        }
        return group;
      },
      {}
    );

    const companyName = employerName.replaceAll(' ', '');
    const timestamp = formatDateTimeISOUtc(getNow());
    const fileName = `${allowanceModel.year}_allowance_model_${companyName}_${timestamp}.csv`;

    const fields = [
      { label: 'ageFrom', value: 'ageFrom' },
      { label: 'ageTo', value: 'ageTo' },
      { label: 'class', value: 'class' },
      { label: 'EE', value: 'EE' },
      { label: 'ES', value: 'ES' },
    ];

    if (hasDependents) {
      fields.push(
        { label: 'EC1', value: 'EC1' },
        { label: 'EC2', value: 'EC2' },
        { label: 'ECmax', value: 'ECmax' },
        { label: 'FA1', value: 'FA1' },
        { label: 'FA2', value: 'FA2' },
        { label: 'FAmax', value: 'FAmax' }
      );
    } else {
      fields.push({ label: 'EC', value: 'EC' }, { label: 'FA', value: 'FA' });
    }

    const sortedData = Object.values(exportableRowByRowId).sort(
      (rowA, rowB) => {
        if (rowA.class !== rowB.class) {
          return rowA.class.localeCompare(rowB.class);
        }
        return rowA.ageFrom - rowB.ageFrom;
      }
    );

    exportToCsv(sortedData, fileName, fields);
  } catch (error) {
    showErrorNotification({
      message: 'An error occurred trying to export allowance model to csv.',
    });
    logger.error(
      `An error occurred trying to export allowance model to csv. employer name: ${employerName}. error: ${error}`
    );
  }
}
